import React, { useEffect } from 'react'
import _ from 'lodash'
import Dygraph from 'dygraphs'

import { barChartPlotter } from '../bar-chart-plotter'
import { TotalConsumptionDataPoint } from '../../common/redux/api/web-api'

export type BarChartProps = {
    barChartId: string
    data: TotalConsumptionDataPoint[]
    useMonth: boolean
    yLabel?: string
}

export function configureChart(
    data: Array<Array<number>>,
    xAxisMapper: { [k: string]: string },
    elementId: string,
    xRangePad: number,
    axisLabelWidth: number,
    showXLabels: boolean,
    labelsDiv: string,
    yLabel: string
): void {
    const container = document.getElementById(elementId)
    if (!container) throw new Error(`Dygraph container (#${elementId}) does not exist`)

    const labelsContainer = document.getElementById(labelsDiv)
    if (!labelsContainer) throw new Error(`Dygraph labels container (#${labelsDiv}) does not exist`)

    new Dygraph(container, data, {
        labels: ['', 'kWh'],
        axisLabelWidth: axisLabelWidth,
        colors: ['#00000'],
        legend: 'always',
        labelsDiv: labelsContainer,
        animatedZooms: true, //
        labelsSeparateLines: false,
        plotter: barChartPlotter,
        drawGrid: true,
        strokeWidth: 3.9, // show border for serial numbers on graph
        highlightCircleSize: 5, //Make bigger dot on graph
        xRangePad: xRangePad,
        ylabel: yLabel,
        legendFormatter: function (dataPoint) {
            if (dataPoint === undefined || dataPoint.x === undefined) return '<br/>'
            return (
                xAxisMapper[dataPoint.x] +
                ': ' +
                '<b>' +
                (typeof dataPoint.series[0].y === 'string'
                    ? parseFloat(dataPoint.series[0].y)
                    : dataPoint.series[0].y
                ).toFixed(2) +
                ' kWh' +
                '</b>'
            )
        },
        axes: {
            x: {
                axisLabelFormatter: function (val) {
                    if (showXLabels) return xAxisMapper[val.toString()]
                    return ''
                },
            },
        },
    })
}

export const BarChart = React.memo(function BarChart({
    barChartId,
    data,
    useMonth = false,
    yLabel = 'kWh',
}: BarChartProps): React.ReactElement {
    const localizeDate = (date: Date | undefined, monthResolution: boolean) => {
        if (date) {
            const options: Intl.DateTimeFormatOptions = monthResolution
                ? { year: 'numeric', month: 'short' }
                : { year: 'numeric', month: 'numeric', day: 'numeric' }
            return date.toLocaleDateString('sv-SE', options)
        } else {
            return '---'
        }
    }

    useEffect(() => {
        if (data?.length) {
            let x = 1
            const xToIdMapper: { [k: string]: string } = {}
            const dyGraphCurrentData: Array<Array<number>> = []
            _.each(data, function (item) {
                const d = new Date(item.timestamp)
                const y = item.totalConsumptionKwh
                xToIdMapper[x] = localizeDate(d, useMonth)
                const dataPoint = [x, y]
                dyGraphCurrentData.push(dataPoint)
                x++
            })

            configureChart(dyGraphCurrentData, xToIdMapper, barChartId, 20, 80, true, 'legend', yLabel)
        }
    }, [data])

    return (
        <div className="row">
            <div className="text-center">
                <div id="legend" className="legend-one-month"></div>
            </div>
            <div
                id={barChartId}
                className="admin-dashboard-graph"
                style={{ width: '95%', padding: 0 }}
                data-testid="bar-chart"
            ></div>
        </div>
    )
})
